import { useEffect, useState } from "react";
import {
  getRooms, getTickets, createRoom, updateRoomFull, deleteRoom,
  unlinkStudentFromRoom, setRoomAvailability, getAllInterests, respondToInterest,
  linkStudentToRoom
} from "../../firebase/services";
import { Badge, Avatar, Spinner, EmptyState, Modal, Input, Btn } from "../../components/UI";
import ContratModal from "../../components/ContratModal";

const PAY = {
  paid:     { label: "Payé ✓",      type: "ok"   },
  pending:  { label: "À confirmer", type: "pend" },
  late:     { label: "En retard",   type: "late" },
  due_soon: { label: "Bientôt dû",  type: "warn" },
  vacant:   { label: "Vacante 🟢",  type: "info" },
};
const EMPTY = { number: "", tenantName: "", tenantEmail: "", tenantPhone: "", loyer: "27500", dueDay: "10" };

// Nettoie un numéro pour wa.me : garde les chiffres, préfixe 223 si numéro local malien (8 chiffres)
function waNumber(phone) {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return digits.length === 8 ? "223" + digits : digits;
}

function timeAgo(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 3600) return `il y a ${Math.round(s/60)} min`;
  if (s < 86400) return `il y a ${Math.round(s/3600)}h`;
  return `il y a ${Math.round(s/86400)}j`;
}

export default function OwnerRooms({ onNavigate }) {
  const [rooms, setRooms]         = useState([]);
  const [tickets, setTickets]     = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState("all");

  const [modal, setModal]               = useState(null);
  const [selected, setSelected]         = useState(null);
  const [contratRoom, setContratRoom]   = useState(null);
  const [form, setForm]                 = useState(EMPTY);
  const [availForm, setAvailForm]       = useState({ availableSoon: false, availableFrom: "", description: "" });
  const [saving, setSaving]             = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [responding, setResponding]     = useState(null);
  const [relinking, setRelinking]       = useState(false);
  const [relinkMsg, setRelinkMsg]       = useState(null);
  const [relinkUid, setRelinkUid]       = useState("");
  const [bulkModal, setBulkModal]       = useState(false);
  const [bulkUids, setBulkUids]         = useState({});   // { roomId: "uid saisi" }
  const [bulkStatus, setBulkStatus]     = useState({});   // { roomId: "ok" | "erreur" | "en_cours" }

  useEffect(() => {
    const unsubs = [
      getRooms(data => { setRooms(data); setLoading(false); }),
      getTickets(setTickets),
      getAllInterests(setInterests),
    ];
    return () => unsubs.forEach(u => u && u());
  }, []);

  const openAdd  = () => { setForm(EMPTY); setSelected(null); setDeleteConfirm(false); setModal("add"); };
  const openEdit = (room, e) => {
    e?.stopPropagation();
    setForm({ number: room.number||"", tenantName: room.tenantName||"", tenantEmail: room.tenantEmail||"", tenantPhone: room.tenantPhone||"", loyer: String(room.loyer||27500), dueDay: String(room.dueDay||10) });
    setSelected(room); setDeleteConfirm(false); setModal("edit");
  };
  const openAvail = (room, e) => {
    e?.stopPropagation();
    setAvailForm({ availableSoon: room.availableSoon||false, availableFrom: room.availableFrom||"", description: room.description||"" });
    setSelected(room); setModal("avail");
  };
  const openInterests = (room, e) => { e?.stopPropagation(); setSelected(room); setModal("interests"); };
  const openDetail    = (room)    => { setSelected(room); setModal("detail"); setRelinkMsg(null); setRelinkUid(""); };

  const handleSave = async () => {
    if (!form.number.trim()) return;
    setSaving(true);
    const data = { number: form.number.trim(), tenantName: form.tenantName.trim(), tenantEmail: form.tenantEmail.trim(), tenantPhone: form.tenantPhone.trim(), loyer: parseInt(form.loyer)||27500, dueDay: parseInt(form.dueDay)||10 };
    if (modal === "add") {
      await createRoom(data);
    } else if (selected) {
      await updateRoomFull(selected.id, { ...data, paymentStatus: data.tenantName ? (selected.paymentStatus !== "vacant" ? selected.paymentStatus : "due_soon") : "vacant" });
    }
    setSaving(false); setModal(null);
  };

  const handleSaveAvail = async () => {
    if (!selected) return;
    setSaving(true);
    await setRoomAvailability(selected.id, availForm);
    setSaving(false); setModal(null);
  };

  const handleSetStatus = async (room, status) => {
    setSaving(true);
    if (status === "vacant") {
      await unlinkStudentFromRoom(room.id, room.tenantId || null);
    } else if (status === "occupied") {
      await updateRoomFull(room.id, { availableSoon: false, availableFrom: "", paymentStatus: room.tenantName ? "due_soon" : "vacant" });
      await setRoomAvailability(room.id, { availableSoon: false, availableFrom: "", description: room.description || "" });
    } else if (status === "soon") {
      await setRoomAvailability(room.id, { availableSoon: true, availableFrom: room.availableFrom || "", description: room.description || "" });
    }
    setSaving(false); setModal(null);
  };

  const handleDelete = async () => {
    if (!selected) return;
    setSaving(true);
    await deleteRoom(selected.id);
    setSaving(false); setModal(null); setDeleteConfirm(false);
  };

  const handleRespond = async (interestId, status) => {
    setResponding(interestId);
    await respondToInterest(interestId, status);
    setResponding(null);
  };

  // Répare la liaison d'UNE chambre — l'UID est copié depuis
  // Firebase Console → Authentication → Users (colonne "User UID")
  const handleRelink = async (room) => {
    const uid = relinkUid.trim();
    if (!uid) {
      setRelinkMsg({ ok: false, text: "Collez d'abord l'UID Firebase Authentication de l'étudiant." });
      return;
    }
    setRelinking(true);
    setRelinkMsg(null);
    try {
      await linkStudentToRoom(room.id, { userId: uid, name: room.tenantName || "", email: room.tenantEmail || "" });
      setRelinkMsg({ ok: true, text: `✓ Liaison réparée pour la chambre ${room.number}.` });
      setRelinkUid("");
    } catch (e) {
      setRelinkMsg({ ok: false, text: e.message || "Échec de la réparation." });
    }
    setRelinking(false);
  };

  // Réparation en masse : l'UID de chaque locataire est saisi une fois
  // (copié depuis Authentication), puis « Lier » écrit users/{uid} et
  // rooms/{roomId}.tenantId d'un coup — aucune Cloud Function requise.
  const handleBulkRelinkOne = async (room) => {
    const uid = (bulkUids[room.id] || "").trim();
    if (!uid) return;
    setBulkStatus(prev => ({ ...prev, [room.id]: "en_cours" }));
    try {
      await linkStudentToRoom(room.id, { userId: uid, name: room.tenantName || "", email: room.tenantEmail || "" });
      setBulkStatus(prev => ({ ...prev, [room.id]: "ok" }));
    } catch (e) {
      setBulkStatus(prev => ({ ...prev, [room.id]: "erreur" }));
    }
  };


  const roomTickets   = (id) => (tickets   || []).filter(t => t.roomId === id && t.status !== "done");
  const roomInterests = (id) => (interests || []).filter(i => i.roomId === id && i.status === "pending");

  const filtered = (rooms || []).filter(r => {
    if (!r) return false;
    if (filter === "late")    return r.paymentStatus === "late";
    if (filter === "tickets") return roomTickets(r.id).length > 0;
    if (filter === "vacant")  return r.paymentStatus === "vacant" || r.availableSoon;
    return true;
  });

  if (loading) return <div style={S.page}><Spinner /></div>;

  return (
    <div style={S.page}>
      <div style={S.topBar}>
        <div>
          <h1 style={S.title}>Chambres</h1>
          <p style={S.sub}>{rooms.length} chambres · {rooms.filter(r => r.paymentStatus === "vacant").length} vacantes</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button style={S.repairBtn} onClick={() => { setBulkModal(true); setBulkResults([]); }} title="Réparer les liaisons compte ↔ chambre">🔧</button>
          <button style={S.addBtn} onClick={openAdd}>+ Ajouter</button>
        </div>
      </div>

      <div style={S.filterBar}>
        {[["all","Toutes"],["late","En retard"],["tickets","Tickets"],["vacant","Disponibles"]].map(([v,l]) => (
          <button key={v} style={{...S.pill,...(filter===v?S.pillOn:{})}} onClick={()=>setFilter(v)}>{l}</button>
        ))}
      </div>

      <div style={S.scroll}>
        {filtered.length === 0 ? (
          <EmptyState icon="🏠" title="Aucune chambre" subtitle='Cliquez "+ Ajouter" pour créer une chambre' />
        ) : filtered.map((room, i) => {
          const at = roomTickets(room.id);
          const it = roomInterests(room.id);
          const ps = PAY[room.paymentStatus] || PAY.late;
          return (
            <div key={room.id} style={S.card} onClick={() => openDetail(room)}>
              <div style={S.cardTop}>
                <Avatar name={room.tenantName || "?"} size={40} index={i} />
                <div style={{ flex: 1 }}>
                  <p style={S.roomNum}>Chambre {room.number}</p>
                  <p style={S.tenant}>{room.tenantName || <em style={{ color: "#aab8c8" }}>Vacant</em>}</p>
                  {room.tenantEmail && <p style={S.email}>{room.tenantEmail}</p>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button style={S.iconBtn} onClick={e => openAvail(room, e)} title="Disponibilité">🏷️</button>
                    <button style={S.iconBtn} onClick={e => openEdit(room, e)}  title="Modifier">✏️</button>
                  </div>
                  {it.length > 0 && (
                    <button style={S.intChip} onClick={e => openInterests(room, e)}>
                      👥 {it.length} candidat{it.length > 1 ? "s" : ""}
                    </button>
                  )}
                </div>
              </div>
              <div style={S.cardBot}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Badge type={ps.type}>{ps.label}</Badge>
                  {room.availableSoon && <Badge type="warn">Bientôt libre</Badge>}
                  {at.length > 0 && <Badge type="open">{at.length} ticket{at.length > 1 ? "s" : ""}</Badge>}
                </div>
                <span style={S.loyer}>{(room.loyer||27500).toLocaleString("fr-FR")} FCFA</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* DÉTAIL */}
      <Modal open={modal==="detail"} onClose={()=>setModal(null)} title={`Chambre ${selected?.number}`}>
        {selected && (
          <div>
            <div style={S.detGrid}>
              <div style={S.dRow}><span style={S.dl}>Locataire</span><span style={S.dv}>{selected.tenantName||"Vacant"}</span></div>
              <div style={S.dRow}><span style={S.dl}>Loyer</span><span style={{...S.dv,fontWeight:700,color:"#1a3a5c"}}>{(selected.loyer||27500).toLocaleString("fr-FR")} FCFA</span></div>
              <div style={S.dRow}><span style={S.dl}>Statut</span><Badge type={(PAY[selected.paymentStatus]||PAY.late).type}>{(PAY[selected.paymentStatus]||PAY.late).label}</Badge></div>
              <div style={S.dRow}><span style={S.dl}>Bientôt libre</span><span style={{...S.dv,color:selected.availableSoon?"#8a4c00":"#5a7a9a"}}>{selected.availableSoon?`Oui — ${selected.availableFrom||"date non définie"}`:"Non"}</span></div>
              {selected.tenantEmail && (
                <div style={S.dRow}>
                  <span style={S.dl}>Liaison compte</span>
                  <span style={{...S.dv, color: selected.tenantId ? "#2d6a0a" : "#a32d2d", fontWeight:600}}>
                    {selected.tenantId ? "✓ OK" : "⚠️ Absente"}
                  </span>
                </div>
              )}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:14}}>
              <Btn variant="primary" onClick={()=>{setModal(null);setTimeout(()=>openAvail(selected,{}),50);}}>🏷️ Gérer la disponibilité</Btn>
              <Btn variant="outline" onClick={()=>{setModal(null);setTimeout(()=>openEdit(selected,{}),50);}}>✏️ Modifier</Btn>
              <Btn variant="secondary" onClick={()=>{setModal(null);onNavigate("chat",selected.id);}}>💬 Conversation</Btn>
              <Btn variant="outline" onClick={()=>{setModal(null);setTimeout(()=>setContratRoom(selected),50);}}>
                📜 {selected.contractUrl ? "Contrat de location (existant)" : "Générer le contrat de location"}
              </Btn>
              {selected.tenantEmail && (
                <div style={{ background:"#f7fafd", borderRadius:12, padding:"12px 12px 4px" }}>
                  <p style={{ fontSize:11.5, color:"#7a90a8", margin:"0 0 8px", lineHeight:1.5 }}>
                    Copiez l'UID depuis <strong>Firebase Console → Authentication → Users</strong> (colonne « User UID » pour {selected.tenantEmail}), collez-le ici :
                  </p>
                  <Input placeholder="Ex: xK9mP2aLqR7tYnB4..." value={relinkUid} onChange={e => setRelinkUid(e.target.value)} />
                  <Btn variant="outline" onClick={()=>handleRelink(selected)} disabled={relinking}>
                    {relinking ? "Réparation..." : "🔗 Réparer la liaison compte ↔ chambre"}
                  </Btn>
                </div>
              )}
              {relinkMsg && (
                <div style={{ background: relinkMsg.ok ? "#e8f5e0" : "#fdecea", color: relinkMsg.ok ? "#2d6a0a" : "#a32d2d", borderRadius:10, padding:"9px 12px", fontSize:12.5, fontWeight:600 }}>
                  {relinkMsg.text}
                </div>
              )}
              {waNumber(selected.tenantPhone) && (
                <a
                  href={`https://wa.me/${waNumber(selected.tenantPhone)}?text=${encodeURIComponent(`Bonjour ${selected.tenantName||""}, IAM Business Group — concernant votre chambre ${selected.number}.`)}`}
                  target="_blank" rel="noreferrer"
                  style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:"#25D366",borderRadius:12,padding:"11px",textDecoration:"none",fontSize:13.5,fontWeight:700,color:"white",fontFamily:"inherit"}}
                >📱 WhatsApp {selected.tenantName?.split(" ")[0] || "locataire"}</a>
              )}
              {selected.tenantName && <Btn variant="ghost" onClick={()=>handleSetStatus(selected,"vacant")} disabled={saving}>🚪 Libérer la chambre</Btn>}
            </div>
          </div>
        )}
      </Modal>

      {/* DISPONIBILITÉ */}
      <Modal open={modal==="avail"} onClose={()=>setModal(null)} title={`Disponibilité — Ch. ${selected?.number}`}>
        {selected && (
          <div>
            <div style={{background:"#e3f1fb",borderRadius:12,padding:"10px 14px",marginBottom:14}}>
              <p style={{fontSize:12,color:"#4a6a8a",margin:0,lineHeight:1.6}}>
                Définissez si cette chambre est <strong>occupée</strong>, <strong>bientôt libre</strong> ou <strong>vacante</strong>. Les étudiants voient les chambres vacantes et bientôt libres.
              </p>
            </div>
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              {[
                {key:"occupied", label:"🔴 Occupée",        active: !selected.availableSoon && selected.paymentStatus!=="vacant"},
                {key:"soon",     label:"🟡 Bientôt libre",  active: selected.availableSoon},
                {key:"vacant",   label:"🟢 Vacante / Libre",active: selected.paymentStatus==="vacant" && !selected.availableSoon},
              ].map(opt => (
                <button key={opt.key}
                  style={{flex:1,padding:"8px 4px",borderRadius:10,border:opt.active?"1.5px solid #2e86c1":"1.5px solid #d0dce8",background:opt.active?"#e3f1fb":"#f5f8fc",color:opt.active?"#1a3a5c":"#5a7a9a",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:opt.active?700:400,textAlign:"center"}}
                  onClick={() => handleSetStatus(selected, opt.key)}
                >{opt.label}</button>
              ))}
            </div>
            <Input label="Date de disponibilité (optionnel)" type="date" value={availForm.availableFrom} onChange={e=>setAvailForm(f=>({...f,availableFrom:e.target.value}))} />
            <div style={{marginBottom:12}}>
              <label style={{fontSize:12,color:"#4a6a8a",display:"block",marginBottom:4,fontWeight:500}}>Description visible par les étudiants</label>
              <textarea style={{width:"100%",border:"1.5px solid #d0dce8",borderRadius:10,padding:"10px 12px",fontSize:13,fontFamily:"inherit",background:"#f7fafd",color:"#1a2a3a",resize:"none",outline:"none",boxSizing:"border-box"}} value={availForm.description} onChange={e=>setAvailForm(f=>({...f,description:e.target.value}))} placeholder="Ex: Chambre meublée, eau incluse..." rows={3} />
            </div>
            <Btn variant="primary" onClick={handleSaveAvail} disabled={saving}>
              {saving?"Enregistrement...":"✓ Enregistrer les infos"}
            </Btn>
          </div>
        )}
      </Modal>

      {/* CANDIDATS */}
      <Modal open={modal==="interests"} onClose={()=>setModal(null)} title={`Candidats — Ch. ${selected?.number}`}>
        {selected && (() => {
          const iList = (interests||[]).filter(i => i.roomId === selected.id);
          return iList.length === 0 ? <EmptyState icon="👥" title="Aucun candidat" subtitle="Personne n'a manifesté d'intérêt" /> : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {iList.map(it => {
                const ss = {pending:{bg:"#fff9e6",color:"#8a6a00",label:"En attente"},accepted:{bg:"#e8f5e0",color:"#2d6a0a",label:"Accepté ✓"},refused:{bg:"#fdecea",color:"#a32d2d",label:"Refusé"}}[it.status]||{bg:"#fff9e6",color:"#8a6a00",label:"?"};
                return (
                  <div key={it.id} style={{background:"#f7fafd",borderRadius:12,padding:"12px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                      <div style={{width:32,height:32,borderRadius:"50%",background:"#e3f1fb",color:"#1a3a5c",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700}}>{(it.userName||"?").charAt(0)}</div>
                      <div style={{flex:1}}>
                        <p style={{fontSize:13,fontWeight:600,color:"#1a2a3a",margin:0}}>{it.userName||"Inconnu"}</p>
                        <p style={{fontSize:11,color:"#7a90a8",margin:0}}>{timeAgo(it.createdAt)}</p>
                      </div>
                      <span style={{fontSize:11,fontWeight:600,borderRadius:20,padding:"2px 9px",background:ss.bg,color:ss.color}}>{ss.label}</span>
                    </div>
                    {it.message && <p style={{fontSize:12,color:"#4a6a8a",fontStyle:"italic",margin:"4px 0 0",paddingLeft:4,borderLeft:"2px solid #d0dce8"}}>"{it.message}"</p>}
                    {it.status === "pending" && (
                      <div style={{display:"flex",gap:8,marginTop:8}}>
                        <button style={{flex:1,padding:"8px",borderRadius:8,background:"#e8f5e0",color:"#2d6a0a",border:"none",fontSize:12,fontWeight:700,cursor:"pointer"}} onClick={()=>handleRespond(it.id,"accepted")} disabled={responding===it.id}>✓ Accepter</button>
                        <button style={{flex:1,padding:"8px",borderRadius:8,background:"#fdecea",color:"#a32d2d",border:"none",fontSize:12,fontWeight:700,cursor:"pointer"}} onClick={()=>handleRespond(it.id,"refused")} disabled={responding===it.id}>✕ Refuser</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Modal>

      {/* AJOUT/ÉDITION */}
      <Modal open={modal==="add"||modal==="edit"} onClose={()=>setModal(null)} title={modal==="add"?"Nouvelle chambre":`Modifier — Ch. ${selected?.number}`}>
        <div>
          <p style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",color:"#7a90a8",margin:"0 0 8px"}}>Chambre</p>
          <div style={{display:"flex",gap:10}}>
            <div style={{flex:1}}><Input label="Numéro *" value={form.number} onChange={e=>setForm(f=>({...f,number:e.target.value}))} placeholder="D101" /></div>
            <div style={{flex:1}}><Input label="Loyer (FCFA)" type="number" value={form.loyer} onChange={e=>setForm(f=>({...f,loyer:e.target.value}))} /></div>
          </div>
          <Input label="Jour d'échéance" type="number" value={form.dueDay} onChange={e=>setForm(f=>({...f,dueDay:e.target.value}))} placeholder="10" />
          <p style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",color:"#7a90a8",margin:"12px 0 8px"}}>Locataire</p>
          <Input label="Nom complet" value={form.tenantName} onChange={e=>setForm(f=>({...f,tenantName:e.target.value}))} placeholder="Amara Diallo" />
          <Input label="Email" type="email" value={form.tenantEmail} onChange={e=>setForm(f=>({...f,tenantEmail:e.target.value}))} placeholder="d101@iam.com" />
          <Input label="Téléphone (WhatsApp)" value={form.tenantPhone} onChange={e=>setForm(f=>({...f,tenantPhone:e.target.value}))} placeholder="+223 70 00 00 00" />
          <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:8}}>
            <Btn variant="primary" onClick={handleSave} disabled={saving||!form.number.trim()}>
              {saving?"Enregistrement...":modal==="add"?"✓ Créer":"✓ Enregistrer"}
            </Btn>
            {modal==="edit" && !deleteConfirm && <Btn variant="danger" onClick={()=>setDeleteConfirm(true)}>🗑️ Supprimer</Btn>}
            {deleteConfirm && (
              <div style={{background:"#fff5f5",border:"1px solid #f5b8b8",borderRadius:10,padding:"12px 14px"}}>
                <p style={{fontSize:13,color:"#a32d2d",fontWeight:600,margin:"0 0 6px"}}>⚠️ Confirmer la suppression ?</p>
                <div style={{display:"flex",gap:8}}>
                  <button style={{flex:1,padding:"9px",borderRadius:8,background:"#a32d2d",color:"white",border:"none",fontSize:13,fontWeight:700,cursor:"pointer"}} onClick={handleDelete} disabled={saving}>Oui</button>
                  <button style={{flex:1,padding:"9px",borderRadius:8,background:"#f0f4f8",color:"#1a3a5c",border:"none",fontSize:13,fontWeight:600,cursor:"pointer"}} onClick={()=>setDeleteConfirm(false)}>Annuler</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* CONTRAT DE LOCATION */}
      <ContratModal
        open={!!contratRoom}
        onClose={() => setContratRoom(null)}
        room={contratRoom ? (rooms.find(r => r.id === contratRoom.id) || contratRoom) : null}
      />

      {/* RÉPARATION EN MASSE DES LIAISONS */}
      <Modal open={bulkModal} onClose={() => setBulkModal(false)} title="Réparer les liaisons compte ↔ chambre">
        <div>
          <p style={{ fontSize:12.5, color:"#4a6a8a", lineHeight:1.6, marginBottom:14 }}>
            Pour chaque chambre, ouvrez <strong>Firebase Console → Authentication → Users</strong>, repérez la ligne dont l'email correspond, copiez son <strong>User UID</strong> et collez-le ci-dessous, puis cliquez « Lier ».
          </p>
          <div style={{ display:"flex", flexDirection:"column", gap:10, maxHeight:420, overflowY:"auto" }}>
            {rooms.filter(r => r.tenantEmail).map(room => {
              const status = bulkStatus[room.id];
              return (
                <div key={room.id} style={{ background:"#f7fafd", borderRadius:10, padding:"10px 12px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:"#1a3a5c" }}>Chambre {room.number}</span>
                    <span style={{ fontSize:11.5, color:"#7a90a8" }}>{room.tenantEmail}</span>
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    <input
                      style={{ flex:1, border:"1.5px solid #d0dce8", borderRadius:8, padding:"7px 9px", fontSize:12, fontFamily:"monospace", background:"white", color:"#1a2a3a", outline:"none" }}
                      placeholder="User UID..."
                      value={bulkUids[room.id] || ""}
                      onChange={e => setBulkUids(prev => ({ ...prev, [room.id]: e.target.value }))}
                    />
                    <button
                      style={{ padding:"7px 12px", borderRadius:8, border:"none", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                        background: status === "ok" ? "#a8d880" : "#1a3a5c", color: status === "ok" ? "#1a3a2c" : "white" }}
                      onClick={() => handleBulkRelinkOne(room)}
                      disabled={status === "en_cours" || status === "ok"}
                    >
                      {status === "ok" ? "✓ Liée" : status === "en_cours" ? "..." : status === "erreur" ? "↻ Réessayer" : "Lier"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <Btn variant="ghost" onClick={() => setBulkModal(false)} style={{ marginTop:14 }}>Fermer</Btn>
        </div>
      </Modal>
    </div>
  );
}

const S = {
  page:    {display:"flex",flexDirection:"column",height:"100%",background:"#f5f8fc",fontFamily:"'Barlow',sans-serif"},
  topBar:  {display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px 6px"},
  title:   {fontSize:22,fontWeight:800,color:"#1a3a5c",fontFamily:"'Barlow Condensed',sans-serif",margin:0},
  sub:     {fontSize:12,color:"#7a90a8",margin:"2px 0 0"},
  addBtn:  {background:"#1a3a5c",color:"white",border:"none",borderRadius:10,padding:"9px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"},
  repairBtn: {background:"#f0f4f8",color:"#1a3a5c",border:"1.5px solid #d0dce8",borderRadius:10,padding:"9px 12px",fontSize:15,cursor:"pointer",fontFamily:"inherit"},
  filterBar:{display:"flex",gap:8,padding:"6px 16px 10px",overflowX:"auto"},
  pill:    {padding:"6px 14px",borderRadius:20,border:"1.5px solid #d0dce8",background:"white",color:"#5a7a9a",fontSize:12,fontWeight:500,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"inherit"},
  pillOn:  {background:"#1a3a5c",color:"white",borderColor:"#1a3a5c"},
  scroll:  {flex:1,overflowY:"auto",padding:"0 16px 80px"},
  card:    {background:"white",borderRadius:14,padding:"14px",marginBottom:10,border:"0.5px solid #d0dce8",cursor:"pointer"},
  cardTop: {display:"flex",alignItems:"center",gap:10,marginBottom:10},
  roomNum: {fontSize:14,fontWeight:700,color:"#1a3a5c",margin:0},
  tenant:  {fontSize:13,color:"#2a4a6a",margin:0},
  email:   {fontSize:11,color:"#7a90a8",margin:"1px 0 0"},
  cardBot: {display:"flex",justifyContent:"space-between",alignItems:"center"},
  loyer:   {fontSize:13,color:"#1a3a5c",fontWeight:700,fontFamily:"'Barlow Condensed',sans-serif"},
  iconBtn: {background:"#f0f4f8",border:"none",fontSize:16,cursor:"pointer",padding:"5px 7px",borderRadius:8},
  intChip: {background:"#e3f1fb",border:"none",color:"#1a3a5c",fontSize:11,fontWeight:600,borderRadius:20,padding:"3px 10px",cursor:"pointer"},
  detGrid: {background:"#f5f8fc",borderRadius:12,marginBottom:4},
  dRow:    {display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 14px",borderBottom:"0.5px solid #e8f0f8"},
  dl:      {fontSize:13,color:"#7a90a8"},
  dv:      {fontSize:13,color:"#1a2a3a"},
};
